const baseDefaults = {
  mandant_name: "",
  mandant_typ: "vermarkter",
  mandant_kurzbeschreibung: "",
  mandant_adresse: "",
  mandant_telefon: "",
  mandant_email: "",
  mandant_erreichbarkeit: "",
  mandant_web: "",
  mandant_datenschutz_url: "",
  technik_kontakt_telefon: "",
  technik_kontakt_email: "",
  kommune_landkreis: "",
  kommune_bundesland: "",
  kommune_ortsteile: "",
  kommune_onlineportal_url: "",
  kommune_mitteilungsblatt_url: "",
  kommune_landratsamt_name: "",
  kommune_landratsamt_url: "",
  kommune_abfall_zustaendig: "",
  kommune_abfall_url: "",
  kommune_bereitschaftsdienste: "",
  kommune_tourismus_url: "",
  bot_name: "",
  bot_anrede: "Sie",
  bot_url_prefix: "",
  bot_kontakt_hinweis: "",
  bot_scope_zusatz: ""
};

const runtimePreviewTokens = new Set([
  "question",
  "context",
  "rewrittenquestion",
  "history"
]);

export default {
  name: "LLMVariables",
  data() {
    return {
      saving: false,
      loadingPrompts: false,
      loadingPreview: false,
      mandantTypeItems: ["vermarkter", "kommune"],
      botAnredeItems: ["du", "Sie"],
      promptTemplates: [],
      selectedPromptShortId: "",
      previewResult: null,
      jumpHighlightTimer: null,
      form: { ...baseDefaults }
    };
  },
  async mounted() {
    await this.initializeForm();
  },
  beforeDestroy() {
    if (this.jumpHighlightTimer) {
      window.clearTimeout(this.jumpHighlightTimer);
      this.jumpHighlightTimer = null;
    }
  },
  computed: {
    selectedPrincipalName() {
      return this.$store.state.selectedPrincipal?.name || "";
    },
    selectedPrincipalId() {
      return this.$store.state.selectedPrincipal?.id || "";
    },
    promptOptions() {
      const options = (this.promptTemplates || [])
        .filter(prompt => !!prompt.shortId)
        .map(prompt => ({
          value: prompt.shortId,
          text: prompt.name || prompt.shortId || prompt._id
        }));

      return [{ value: "", text: "-" }, ...options];
    },
    selectedPrompt() {
      return (
        this.promptTemplates.find(
          prompt => prompt.shortId === this.selectedPromptShortId
        ) || null
      );
    },
    previewSections() {
      const prompt = this.previewResult || {};
      const sourcePrompt = this.selectedPrompt || {};
      const sections = [
        {
          key: "prompt",
          title: "Prompt",
          template: prompt.prompt || "",
          sourceTemplate: sourcePrompt.prompt || prompt.prompt || ""
        },
        {
          key: "promptSystem",
          title: "Prompt System",
          template: prompt.promptSystem || "",
          sourceTemplate: sourcePrompt.promptSystem || prompt.promptSystem || ""
        },
        {
          key: "promptUser",
          title: "Prompt User",
          template: prompt.promptUser || "",
          sourceTemplate: sourcePrompt.promptUser || prompt.promptUser || ""
        }
      ];

      return sections.map(section => {
        const highlighted = this.buildHighlightedPreview(
          section.sourceTemplate,
          section.template
        );

        return {
          ...section,
          hasTemplate: String(section.template || "").trim().length > 0,
          renderedHtml: highlighted.html,
          unresolvedTokenCount: highlighted.unresolvedTokenCount,
          tokenBuckets: highlighted.tokenBuckets
        };
      });
    },
    unresolvedTokenCount() {
      return this.previewSections.reduce(
        (sum, section) => sum + Number(section.unresolvedTokenCount || 0),
        0
      );
    },
    legendTokenBuckets() {
      const buckets = {
        replaced: new Map(),
        pending: new Map(),
        missing: new Map()
      };

      this.previewSections.forEach(section => {
        const tokenBuckets = section.tokenBuckets || {};
        this.appendLegendTokens(buckets.replaced, tokenBuckets.replaced || []);
        this.appendLegendTokens(buckets.pending, tokenBuckets.pending || []);
        this.appendLegendTokens(buckets.missing, tokenBuckets.missing || []);
      });

      return {
        replaced: Array.from(buckets.replaced.values()),
        pending: Array.from(buckets.pending.values()),
        missing: Array.from(buckets.missing.values())
      };
    },
    replacedLegendLabel() {
      const tokens = this.legendTokenBuckets.replaced;
      return `Ersetzt (${tokens.length}): ${this.buildLegendTokenList(tokens)}`;
    },
    pendingLegendLabel() {
      const tokens = this.legendTokenBuckets.pending;
      const count = tokens.length;
      return (
        `Laufzeit-Platzhalter (${count}): ` + this.buildLegendTokenList(tokens)
      );
    },
    missingLegendTokens() {
      return this.legendTokenBuckets.missing;
    },
    missingLegendIntro() {
      return `Nicht ersetzte Platzhalter (${this.missingLegendTokens.length}):`;
    },
    isKommune() {
      return this.form.mandant_typ === "kommune";
    }
  },
  watch: {
    async selectedPrincipalId() {
      await this.initializeForm();
    },
    async selectedPromptShortId() {
      await this.loadPromptPreview();
    }
  },
  methods: {
    appendLegendTokens(targetMap, tokens) {
      (tokens || []).forEach(tokenName => {
        const normalizedName = this.normalizeTokenName(tokenName);
        if (!normalizedName || targetMap.has(normalizedName)) return;
        targetMap.set(normalizedName, tokenName);
      });
    },
    formatTokenName(tokenName) {
      return `{{${tokenName}}}`;
    },
    buildLegendTokenList(tokens) {
      if (!Array.isArray(tokens) || !tokens.length) return "-";
      return tokens
        .map(tokenName => this.formatTokenName(tokenName))
        .join(", ");
    },
    findInputByTokenHint(tokenName) {
      const targetPlaceholder = this.formatTokenName(tokenName).toLowerCase();
      const inputNodes = Array.from(this.$el.querySelectorAll(".v-input"));
      return (
        inputNodes.find(node =>
          String(node.textContent || "")
            .toLowerCase()
            .includes(targetPlaceholder)
        ) || null
      );
    },
    highlightJumpTarget(fieldRoot) {
      if (!fieldRoot) return;

      fieldRoot.classList.remove("preview-scroll-target");
      // Reflow, damit die Animation bei jedem Klick neu startet.
      void fieldRoot.offsetWidth;
      fieldRoot.classList.add("preview-scroll-target");

      if (this.jumpHighlightTimer) {
        window.clearTimeout(this.jumpHighlightTimer);
      }
      this.jumpHighlightTimer = window.setTimeout(() => {
        fieldRoot.classList.remove("preview-scroll-target");
        this.jumpHighlightTimer = null;
      }, 1000);
    },
    scrollToMissingTokenField(tokenName) {
      const fieldRoot = this.findInputByTokenHint(tokenName);
      if (!fieldRoot) {
        const formTop = this.$el.querySelector("#llm-variables__form-column");
        if (formTop) {
          formTop.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        return;
      }

      const topOffset = 120;
      const targetTop =
        fieldRoot.getBoundingClientRect().top + window.pageYOffset - topOffset;
      window.scrollTo({
        top: Math.max(targetTop, 0),
        behavior: "smooth"
      });

      this.highlightJumpTarget(fieldRoot);

      const focusTarget = fieldRoot.querySelector(
        "input, textarea, .v-select__selections"
      );
      if (focusTarget && typeof focusTarget.focus === "function") {
        if (focusTarget.classList?.contains("v-select__selections")) {
          focusTarget.click();
        }
        try {
          focusTarget.focus({ preventScroll: true });
        } catch (err) {
          focusTarget.focus();
        }
      }
    },
    escapeRegExp(value) {
      return String(value || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    },
    escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    },
    normalizeTokenName(value) {
      return String(value || "")
        .trim()
        .toLowerCase();
    },
    isUnresolvedToken(tokenName, value) {
      const unresolvedPattern = new RegExp(
        `^\\{\\{\\s*${this.escapeRegExp(tokenName)}\\s*}}$`,
        "i"
      );
      return unresolvedPattern.test(String(value || "").trim());
    },
    getTokenClass(tokenName, tokenValue) {
      const isUnresolved = this.isUnresolvedToken(tokenName, tokenValue);
      if (!isUnresolved) return "preview-token-replaced";

      if (runtimePreviewTokens.has(this.normalizeTokenName(tokenName))) {
        return "preview-token-pending";
      }

      return "preview-token-missing";
    },
    createHighlightResult() {
      return {
        html: "",
        unresolvedTokenCount: 0,
        tokenBuckets: {
          replaced: [],
          pending: [],
          missing: []
        }
      };
    },
    addTokenToBucket(bucket, tokenName) {
      const normalizedName = this.normalizeTokenName(tokenName);
      if (!normalizedName) return;

      const alreadyExists = bucket.some(
        value => this.normalizeTokenName(value) === normalizedName
      );
      if (!alreadyExists) {
        bucket.push(tokenName);
      }
    },
    extractTemplateParts(value) {
      const text = String(value || "");
      const tokenPattern = /\{\{\s*([a-zA-Z0-9_]+)\s*}}/g;
      const parts = [];
      const tokens = [];
      let lastIndex = 0;
      let match = tokenPattern.exec(text);

      while (match) {
        if (match.index > lastIndex) {
          parts.push({
            type: "text",
            value: text.slice(lastIndex, match.index)
          });
        }

        const token = {
          name: match[1],
          raw: match[0]
        };
        parts.push({ type: "token", token });
        tokens.push(token);

        lastIndex = tokenPattern.lastIndex;
        match = tokenPattern.exec(text);
      }

      if (lastIndex < text.length) {
        parts.push({ type: "text", value: text.slice(lastIndex) });
      }

      return { parts, tokens };
    },
    highlightUnresolvedTokens(value) {
      const text = String(value || "").trim();
      if (!text) return this.createHighlightResult();

      const tokenPattern = /\{\{\s*([a-zA-Z0-9_]+)\s*}}/g;
      let rendered = "";
      let lastIndex = 0;
      let unresolvedTokenCount = 0;
      const tokenBuckets = {
        replaced: [],
        pending: [],
        missing: []
      };
      let match = tokenPattern.exec(text);

      while (match) {
        const [token, tokenName] = match;
        rendered += this.escapeHtml(text.slice(lastIndex, match.index));
        const tokenClass = this.getTokenClass(tokenName, token);
        if (tokenClass === "preview-token-missing") {
          unresolvedTokenCount += 1;
          this.addTokenToBucket(tokenBuckets.missing, tokenName);
        } else if (tokenClass === "preview-token-pending") {
          this.addTokenToBucket(tokenBuckets.pending, tokenName);
        } else {
          this.addTokenToBucket(tokenBuckets.replaced, tokenName);
        }
        rendered += `<span class="${tokenClass}">${this.escapeHtml(
          token
        )}</span>`;

        lastIndex = tokenPattern.lastIndex;
        match = tokenPattern.exec(text);
      }

      rendered += this.escapeHtml(text.slice(lastIndex));
      return {
        html: rendered,
        unresolvedTokenCount,
        tokenBuckets
      };
    },
    buildHighlightedPreview(sourceTemplate, renderedTemplate) {
      const renderedText = String(renderedTemplate || "").trim();
      if (!renderedText) return this.createHighlightResult();

      const sourceText = String(sourceTemplate || "");
      const { parts, tokens } = this.extractTemplateParts(sourceText);
      if (!tokens.length) {
        return {
          html: this.escapeHtml(renderedText),
          unresolvedTokenCount: 0,
          tokenBuckets: {
            replaced: [],
            pending: [],
            missing: []
          }
        };
      }

      const matcherPattern =
        "^" +
        parts
          .map(part => {
            if (part.type === "text") {
              return this.escapeRegExp(part.value);
            }
            return "([\\s\\S]*?)";
          })
          .join("") +
        "$";

      const matcher = new RegExp(matcherPattern);
      const matched = renderedText.match(matcher);

      if (!matched) {
        return this.highlightUnresolvedTokens(renderedText);
      }

      let captureIndex = 1;
      let html = "";
      let unresolvedTokenCount = 0;
      const tokenBuckets = {
        replaced: [],
        pending: [],
        missing: []
      };
      parts.forEach(part => {
        if (part.type === "text") {
          html += this.escapeHtml(part.value);
          return;
        }

        const tokenValue = matched[captureIndex] ?? "";
        captureIndex += 1;
        const tokenClass = this.getTokenClass(part.token.name, tokenValue);
        if (tokenClass === "preview-token-missing") {
          unresolvedTokenCount += 1;
          this.addTokenToBucket(tokenBuckets.missing, part.token.name);
        } else if (tokenClass === "preview-token-pending") {
          this.addTokenToBucket(tokenBuckets.pending, part.token.name);
        } else {
          this.addTokenToBucket(tokenBuckets.replaced, part.token.name);
        }
        const tokenOutput = tokenValue || part.token.raw;
        html += `<span class="${tokenClass}">${this.escapeHtml(
          tokenOutput
        )}</span>`;
      });

      return {
        html,
        unresolvedTokenCount,
        tokenBuckets
      };
    },
    resolveDefaultsByPrincipal() {
      return {
        ...baseDefaults,
        mandant_name: this.selectedPrincipalName
      };
    },
    resetToPrincipalDefaults() {
      this.form = {
        ...baseDefaults,
        ...this.resolveDefaultsByPrincipal()
      };
    },
    async initializeForm() {
      this.resetToPrincipalDefaults();
      await Promise.all([this.loadVariables(), this.loadPromptTemplates()]);
      this.ensureSelectedPrompt();
      await this.loadPromptPreview();
    },
    async loadPromptTemplates() {
      if (!this.selectedPrincipalId) {
        this.promptTemplates = [];
        return;
      }

      this.loadingPrompts = true;
      try {
        const response = await this.$store.dispatch("getLLMPrompts", {
          principal_id: this.selectedPrincipalId
        });
        this.promptTemplates = Array.isArray(response) ? response : [];
      } catch (e) {
        this.promptTemplates = [];
      } finally {
        this.loadingPrompts = false;
      }
    },
    ensureSelectedPrompt() {
      if (!this.promptTemplates.length) {
        this.selectedPromptShortId = "";
        return;
      }

      const promptOptionsWithShortId = this.promptOptions.filter(
        option => option.value
      );

      if (!promptOptionsWithShortId.length) {
        this.selectedPromptShortId = "";
        return;
      }

      const selectedStillExists =
        !!this.selectedPromptShortId &&
        this.promptTemplates.some(
          prompt => prompt.shortId === this.selectedPromptShortId
        );

      if (!selectedStillExists) {
        this.selectedPromptShortId = "";
      }
    },
    async loadPromptPreview() {
      if (!this.selectedPrincipalId || !this.selectedPromptShortId) {
        this.previewResult = null;
        return;
      }

      this.loadingPreview = true;
      try {
        this.previewResult = await this.$store.dispatch("getLLMPromptPreview", {
          promptShortId: this.selectedPromptShortId,
          principal_id: this.selectedPrincipalId
        });
      } catch (e) {
        this.previewResult = null;
      } finally {
        this.loadingPreview = false;
      }
    },
    async loadVariables() {
      if (!this.selectedPrincipalId) return;

      try {
        const response = await this.$store.dispatch("getLLMVariables", {
          principal_id: this.selectedPrincipalId
        });
        const persistedVariables = response?.variables;

        if (persistedVariables && typeof persistedVariables === "object") {
          this.form = {
            ...this.form,
            ...persistedVariables
          };
        }
      } catch (e) {
        // Fehler wird bereits zentral via createError angezeigt.
      }
    },
    async saveVariables() {
      if (!this.selectedPrincipalId) {
        await this.$store.dispatch("createError", {
          text: "Kein Mandant ausgewaehlt. Speichern nicht moeglich."
        });
        return;
      }

      this.saving = true;
      try {
        const response = await this.$store.dispatch(
          "saveLLMVariables",
          this.form
        );

        if (!response) {
          await this.$store.dispatch("createError", {
            text: "Speichern fehlgeschlagen. Bitte erneut versuchen."
          });
          return;
        }

        if (response?.variables && typeof response.variables === "object") {
          this.form = {
            ...baseDefaults,
            ...this.resolveDefaultsByPrincipal(),
            ...response.variables
          };
        }

        await this.$store.dispatch(
          "createSuccess",
          "Variablen wurden gespeichert."
        );

        // Refresh preview only when a prompt is actively selected.
        if (this.selectedPromptShortId) {
          await this.loadPromptPreview();
        }
      } catch (e) {
        // Fehler wird bereits zentral via createError angezeigt.
      } finally {
        this.saving = false;
      }
    }
  }
};
